import _ from 'lodash';
import moment, { duration } from 'moment';

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

export default class TimeConversionUtils {
  static secondsToTimeMask(totalSeconds, timeMask = 'HH:MM') {
    const mask = timeMask.toUpperCase();
    let hours = Math.floor(totalSeconds / 3600);
    let mins = (Math.floor((totalSeconds - (hours * 3600)) / 60));
    let secs = Math.floor(totalSeconds - (hours * 3600) - (mins * 60));

    if (hours < 10) {
      hours = _.padStart(hours, 2, 0);
    }

    if (mins < 10) {
      if (hours) {
        mins = _.padStart(mins, 2, 0);
      }
    }

    if (secs < 10) {
      secs = _.padStart(secs, 2, 0);
    }

    let val = `${hours}:${mins}`;
    if (mask.indexOf(':SS') !== -1) {
      val += `:${secs}`;
    }

    return val;
  }

  /**
   * Takes a timestamp in a formatted string like HH:MM or HH:MM:SS
   * and returns the total seconds for that.
   * This is meant to be the inverse of `secondsToTimeMask()` in this class.
   * @param maskedString
   */
  static maskToSeconds(maskedString) {
    if (!maskedString) { return 0; }
    if (Number.isInteger(maskedString)) { return maskedString; }
    if (maskedString.indexOf(':') === -1) { return 0; }

    const normalize = str => str.replace(/[^0-9]/, 0);
    const toNum = str => parseInt(str, 10) || 0;
    const stringParts = maskedString.split(':');

    const hourSecs = toNum(normalize(stringParts[0])) * 60 * 60;
    const minSecs = toNum(normalize(stringParts[1])) * 60;
    const secSecs = toNum(normalize(stringParts[2] || '0'));

    return hourSecs + minSecs + secSecs;
  }

  static prettyPrintDurationHoursMinutes(d) {
    if (Number.isNaN(d)) {
      return '-';
    }
    const momentDuration = moment.duration(d, 'seconds');
    const hours = TimeConversionUtils.paddedString(momentDuration.hours());
    const minutes = TimeConversionUtils.paddedString(momentDuration.minutes());
    const days = momentDuration.days();
    if (days > 0) {
      return `${days}D ${hours}:${minutes}`;
    }

    return `${hours}:${minutes}`;
  }

  /**
   * Prints just the hours/minutes/seconds components of a duration.
   * This will only work as expected if the duration is less than 24 hours.
   * See the moment documentation here: https://momentjs.com/docs/#/durations/hours/
   * For example, hours() is the hours part of the duration 0-23. If your duration
   * is 36 hours this method will return 12:00:00.
   * @param rawDuration
   * @param units
   * @returns {string}
   */
  static prettyPrintDurationSingleDay(rawDuration, units = 'hours') {
    const duration = moment.duration(rawDuration, units);
    return TimeConversionUtils.prettyPrintDurationObjectSingleDay(duration);
  }

  /**
   * This will only work as expected if the duration is less than 24 hours.
   * See the moment documentation here: https://momentjs.com/docs/#/durations/hours/
   * For example, hours() is the hours part of the duration 0-23. If your duration
   * is 36 hours this method will return 12:00:00.
   * @param momentDuration
   * @returns {string}
   */
  static prettyPrintDurationObjectSingleDay(momentDuration) {
    const hours = TimeConversionUtils.paddedString(momentDuration.hours());
    const minutes = TimeConversionUtils.paddedString(momentDuration.minutes());
    const seconds = TimeConversionUtils.paddedString(momentDuration.seconds());

    if (hours === '00') {
      return `${minutes}:${seconds}`;
    }

    return `${hours}:${minutes}:${seconds}`;
  }

  /**
   * Prints a multi-day duration in the format "X days hh:mm:ss" if more than 1
   * day. Prints just "hh:mm:ss" if less than 1 day.
   * @param momentDuration
   * @returns {string}
   */
  static prettyPrintDurationObjectMultipleDays(momentDuration) {
    let retVal = TimeConversionUtils.prettyPrintDurationObjectSingleDay(momentDuration);
    const days = momentDuration.asDays();
    if (days >= 1) {
      const dayText = days >= 2 ? 'days' : 'day';
      retVal = `${Math.trunc(days)} ${dayText} ${retVal}`;
    }
    return retVal;
  }

  static prettyPrintFullDurationHoursMinutesSeconds(momentDuration) {
    const retVal = TimeConversionUtils.prettyPrintDurationMinutesSeconds(momentDuration);
    const hours = Math.trunc(momentDuration.asHours());
    return `${hours}:${retVal}`;
  }

  /**
   * Just print the minutes and seconds component of the duration.
   * @param momentDuration
   */
  static prettyPrintDurationMinutesSeconds(momentDuration) {
    const minutes = TimeConversionUtils.paddedString(momentDuration.minutes());
    const seconds = TimeConversionUtils.paddedString(momentDuration.seconds());

    if ((Number.isNaN(minutes) || minutes === 'NaN') || (Number.isNaN(seconds) || seconds === 'NaN')) {
      return '00:00';
    }

    return `${minutes}:${seconds}`;
  }

  /**
   * Print the pace as mm:ss unless there are non-zero hours then print as
   * hh:mm:ss. If there are non-zero days print as "X days hh:mm:ss".
   * @param pace
   * @param units
   * @returns {string}
   */
  static prettyPrintPace(pace, units = 'minutes') {
    const d = moment.duration(pace, units);
    return TimeConversionUtils.prettyPrintPaceDurationObject(d);
  }

  /**
   * Print the duration as mm:ss unless there are non-zero hours then print as
   * hh:mm:ss. If there are non-zero days print as "X days hh:mm:ss".
   * @param momentDuration
   * @returns {string}
   */
  static prettyPrintPaceDurationObject(momentDuration) {
    let retVal = '--';
    const days = Math.trunc(momentDuration.asDays());
    const hours = momentDuration.hours();
    const minutes = momentDuration.minutes();
    const seconds = momentDuration.seconds();

    if (!Number.isNaN(minutes) && !Number.isNaN(seconds)) {
      if (!hours && !days) {
        retVal = TimeConversionUtils.prettyPrintDurationMinutesSeconds(momentDuration);
      } else if (hours && !days) {
        retVal = TimeConversionUtils.prettyPrintDurationObjectSingleDay(momentDuration);
      } else if (days) {
        retVal = TimeConversionUtils.prettyPrintDurationObjectMultipleDays(momentDuration);
      }
    }

    return retVal;
  }

  /**
   * @param `day` should be a Date instance.
   * @returns {string}
   */
  static formatDateAsLocaleDateString(day) {
    return day.toLocaleDateString('en-US', {
      year: 'numeric',
      day: '2-digit',
      month: '2-digit',
    });
  }

  static paddedString(number) {
    return number.toString().padStart(2, '0');
  }

  /**
   * Comparer for moment dates in ascending order
   * @param a
   * @param b
   * @returns {number}
   */
  static compareMomentDateAsc(a, b) {
    if (a.isAfter(b)) {
      return 1;
    }
    if (a.isBefore(b)) {
      return -1;
    }
    return 0;
  }

  /**
   * Comparer for moment dates in descending order
   * @param a
   * @param b
   * @returns {number}
   */
  static compareMomentDatesDesc(a, b) {
    return -1 * TimeConversionUtils.compareMomentDateAsc(a, b);
  }

  /**
   * True if the arguments represent the same day. False otherwise.
   * @param d1 something moment can turn into a date
   * @param d2 something moment can turn into a date
   * @returns {boolean}
   */
  static sameDay(d1, d2) {
    const d1Date = moment(d1).toDate();
    const d2Date = moment(d2).toDate();
    return d1Date.getFullYear() === d2Date.getFullYear()
      && d1Date.getMonth() === d2Date.getMonth()
      && d1Date.getDate() === d2Date.getDate();
  }
}
